import { DropdownGroup, Tooltip, TooltipTrigger } from '@toggle/design-system';
import { isJapaneseLocale } from '@toggle/helpers';
import React, { useState } from 'react';

import { mapNavigationKeyToPathname } from '~/components/app-header/utils/navigation-utils';
import { HeaderCategoryItem } from '../category-group/HeaderCategoryGroup';
import { AlfredNavItem } from './AlfredNavItem';
import * as S from './NavItem.styles';

export type NavItemProps = {
  item: HeaderCategoryItem;
  activeKey?: string;
  disabled?: boolean;
  showBottomBorder?: boolean;
};

export const NavItem = ({ item, activeKey, disabled }: NavItemProps) => {
  const isActive = activeKey === item.key;
  const isJapanese = isJapaneseLocale();
  const [isCustomDropdownOpen, setIsCustomDropdownOpen] = useState(false);
  const handleCloseCustomDropdown = () => setIsCustomDropdownOpen(false);

  const customDropdownContent =
    typeof item.customDropdownContent === 'function'
      ? item.customDropdownContent(handleCloseCustomDropdown)
      : item.customDropdownContent;

  if (item.isAlfred && item.isDashboardUpdatesEnabled) {
    return (
      <Tooltip
        label={item.label}
        disabled={!item.showTooltip}
        trigger={TooltipTrigger.Hover}
        placement="bottom"
      >
        <S.ItemWrapper key={item.key} $isActive={isActive}>
          <AlfredNavItem
            item={item}
            disabled={disabled}
            isActive={isActive}
            isLoading={item.isProcessing}
            isDefault={!item.isProcessing}
            isFinished={item.isFinished}
            unreadConversations={item.unreadConversations}
            onConversationClick={item.onConversationClick}
          />
          {item.notification && (
            <S.NotificationWrapper $right={0} $top={-5}>
              {item.notification}
            </S.NotificationWrapper>
          )}
        </S.ItemWrapper>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label={item.label}
      disabled={!item.showTooltip}
      trigger={TooltipTrigger.Hover}
      placement="bottom"
    >
      <S.ItemWrapper key={item.key} $isActive={isActive}>
        {customDropdownContent ? (
          <S.StyledCustomDropdown
            key={item.key}
            align="end"
            modal={false}
            open={isCustomDropdownOpen}
            onOpenChange={setIsCustomDropdownOpen}
            triggerNode={
              <S.ButtonItem
                data-testid={`header-navigation-item-${item.key}`}
                $disabled={disabled}
              >
                <S.ItemLeft>
                  <S.IconWrapper>{item.icon}</S.IconWrapper>
                </S.ItemLeft>
              </S.ButtonItem>
            }
          >
            {customDropdownContent}
          </S.StyledCustomDropdown>
        ) : item.dropdownItems?.length ? (
          <S.StyledDropdown
            key={item.key}
            triggerNode={
              <S.ButtonItem data-testid={`header-navigation-item-${item.key}`}>
                <S.ItemLeft>
                  <S.IconWrapper>{item.icon}</S.IconWrapper>
                </S.ItemLeft>
              </S.ButtonItem>
            }
          >
            {item.dropdownItems.map((group, key) => (
              <DropdownGroup items={group} key={key} />
            ))}
          </S.StyledDropdown>
        ) : (
          <S.ButtonItem
            as="a"
            href={mapNavigationKeyToPathname[item.key]}
            data-testid={`header-navigation-item-${item.key}`}
            $isActive={isActive}
            $pillStyle={!!item.pillStyle}
            $isAlfred={item.isAlfred}
            $isJapanese={isJapanese}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            onClick={e => {
              e.preventDefault();

              if (disabled) {
                return;
              }

              item.onClick(item, e);
            }}
          >
            <S.ItemLeft>
              {item.icon && <S.IconWrapper>{item.icon}</S.IconWrapper>}
              {item.label && !item.showTooltip && (
                <S.MainLabel $isAlfred={item.isAlfred} $isJapanese={isJapanese}>
                  {item.label}
                </S.MainLabel>
              )}
            </S.ItemLeft>
          </S.ButtonItem>
        )}
        {item.notification && (
          <S.NotificationWrapper $right={0} $top={-5}>
            {item.notification}
          </S.NotificationWrapper>
        )}
      </S.ItemWrapper>
    </Tooltip>
  );
};

NavItem.Styled = S;
